<template>
  <div class="min-h-screen bg-slate-900 text-white selection:bg-blue-500 selection:text-white overflow-hidden relative">
    <TornadoTransition :isActive="isLeaving" />
    
    <div :class="{ 'sucked-in': isLeaving }" class="w-full h-full">
      <!-- 全屏动态背景 -->
      <div class="dynamic-bg"></div>
      <div class="overlay-pattern"></div>
      
      <!-- 天气特效 -->
      <WeatherEffect :mouseX="mouseX" :mouseY="mouseY" />

      <!-- Hero Section -->
      <HeroSection @scroll-to-projects="scrollToProjects" />

      <!-- Features / About Section -->
      <FeaturesSection />

      <!-- Projects Section -->
      <section id="projects" class="py-20 bg-slate-800/30">
        <div class="container mx-auto px-6">
          <div class="mb-16 text-center">
            <h2 class="text-3xl md:text-4xl font-bold mb-4">最新项目</h2>
            <div class="w-20 h-1 bg-purple-500 mx-auto rounded-full"></div>
            <p class="mt-4 text-slate-400">探索我在不同领域的一些实践与作品</p>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <div 
              v-for="(project, index) in projects" 
              :key="index"
              class="group bg-slate-800 rounded-2xl overflow-hidden border border-slate-700 hover:border-purple-500/50 transition-all hover:shadow-2xl hover:shadow-purple-500/10 hover:-translate-y-2"
            >
              <div class="h-48 bg-gradient-to-br from-slate-700 to-slate-600 relative overflow-hidden group-hover:from-purple-900/40 group-hover:to-blue-900/40 transition-colors">
                <!-- Placeholder for project image -->
                <div class="absolute inset-0 flex items-center justify-center text-slate-500 group-hover:text-slate-300 transition-colors">
                   <svg xmlns="http://www.w3.org/2000/svg" class="h-16 w-16 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
              </div>
              <div class="p-6">
                <h3 class="text-xl font-bold mb-3 group-hover:text-purple-400 transition-colors">{{ project.title }}</h3>
                <p class="text-slate-400 mb-6 line-clamp-3 text-sm leading-relaxed">
                  {{ project.description }}
                </p>
                <div class="flex flex-wrap gap-2">
                  <span 
                    v-for="(tag, tagIndex) in project.tags" 
                    :key="tagIndex" 
                    class="px-3 py-1 text-xs font-medium rounded-full bg-slate-700/50 text-slate-300 border border-slate-600"
                  >
                    {{ tag }}
                  </span>
                </div>
              </div>
            </div>
          </div>
          
          <div class="mt-12 text-center">
               <a href="/project" class="inline-flex items-center justify-center px-6 py-3 border border-slate-600 rounded-full text-slate-300 hover:bg-slate-800 hover:text-white transition-colors">
                查看更多项目
              </a>
          </div>
        </div>
      </section>

      <!-- Contact / Footer Section -->
      <footer id="contact" class="py-20 bg-slate-900 border-t border-slate-800">
        <div class="container mx-auto px-6 text-center">
          <h2 class="text-3xl font-bold mb-8">保持联系</h2>
          <p class="text-slate-400 mb-10 max-w-xl mx-auto">
            无论是技术交流、项目合作，还是只是想打个招呼，我都非常欢迎！
          </p>
          
          <div class="flex justify-center gap-6 mb-12">
            <a href="https://github.com/UR-xiaoyang" target="_blank" class="p-4 bg-slate-800 rounded-full hover:bg-slate-700 hover:text-white text-slate-400 transition-all hover:scale-110">
              <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path fill-rule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clip-rule="evenodd" />
              </svg>
            </a>
            <a href="mailto:xiaoyang@ur-xiaoyang.com" class="p-4 bg-slate-800 rounded-full hover:bg-slate-700 hover:text-white text-slate-400 transition-all hover:scale-110">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
              </svg>
            </a>
            <a href="https://space.bilibili.com/98621956" target="_blank" class="p-4 bg-slate-800 rounded-full hover:bg-slate-700 hover:text-pink-400 text-slate-400 transition-all hover:scale-110">
               <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                 <path d="M17.813 4.653h.854c1.51.054 2.769.578 3.773 1.574 1.004.995 1.524 2.249 1.56 3.758v6.844c-.036 1.51-.556 2.769-1.56 3.773s-2.262 1.524-3.773 1.56H5.333c-1.51-.036-2.769-.556-3.773-1.56S.036 18.34.002 16.83V8.427c.036-1.51.556-2.769 1.56-3.773s2.262-1.524 3.773-1.56h.854c.205-.52.818-1.55 1.84-3.11.83-1.258 1.43-1.92 1.8-1.98.39-.06.76.08 1.12.43.34.33.47.69.4.99-.06.34-.69 1.29-1.89 2.87l-.46.59h5.42l-.46-.59c-1.2-1.58-1.83-2.53-1.89-2.87-.07-.3.06-.66.4-.99.36-.35.73-.49 1.12-.43.37.06.97.72 1.8 1.98 1.02 1.56 1.63 2.59 1.84 3.11zM6.667 9.87c-.63 0-1.15.22-1.56.66-.41.43-.61.96-.61 1.58 0 .62.2 1.15.61 1.58.41.44.93.66 1.56.66.63 0 1.15-.22 1.56-.66.41-.43.61-.96.61-1.58 0-.62-.2-1.15-.61-1.58-.41-.44-.93-.66-1.56-.66zm10.666 0c-.63 0-1.15.22-1.56.66-.41.43-.61.96-.61 1.58 0 .62.2 1.15.61 1.58.41.44.93.66 1.56.66.63 0 1.15-.22 1.56-.66.41-.43.61-.96.61-1.58 0-.62-.2-1.15-.61-1.58-.41-.44-.93-.66-1.56-.66z"/>
              </svg>
            </a>
          </div>
          
          <p class="text-slate-600 text-sm">
            © {{ new Date().getFullYear() }} xiaoyang. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  </div>
</template>

<script setup>
import HeroSection from '@/components/HeroSection.vue'
import FeaturesSection from '@/components/FeaturesSection.vue'
import WeatherEffect from '@/components/WeatherEffect.vue'
import TornadoTransition from '@/components/TornadoTransition.vue'
import { ref, onMounted, onUnmounted } from 'vue'
import { onBeforeRouteLeave } from 'vue-router'
import { uiState } from '@/state/ui'

const isLeaving = ref(false)

// 路由守卫：离场动画
onBeforeRouteLeave((to, from, next) => {
  // 如果是前往非主页页面，触发动画
  isLeaving.value = true
  uiState.isTornadoActive = true
  
  // 动画持续时间
  setTimeout(() => {
    next()
  }, 2000)
})

const projects = [
  {
    title: '个人网站',
    description: '基于Vue.js构建的个人展示网站，采用现代化设计风格，集成文档中心与项目展示功能。使用 Tailwind CSS 进行重构，提升了 UI 表现力和响应式体验。',
    tags: ['Vue.js', 'Tailwind CSS', 'Vite']
  },
  {
    title: '智能对话助手',
    description: '集成先进 NLP 模型的智能对话系统，能够进行自然语言理解与生成。支持多轮对话、上下文记忆，可应用于客服、教育等场景。',
    tags: ['Python', 'NLP', 'FastAPI']
  },
  {
    title: '数据可视化平台',
    description: '企业级数据分析大屏，提供丰富的数据图表组件与实时数据刷新能力。支持拖拽式布局与自定义数据源配置。',
    tags: ['React', 'D3.js', 'TypeScript']
  }
]

const mouseX = ref(0)
const mouseY = ref(0)

const handleMouseMove = (e) => {
  mouseX.value = e.clientX
  mouseY.value = e.clientY
}

const scrollToProjects = () => {
  const projectsSection = document.getElementById('projects')
  if (projectsSection) {
    projectsSection.scrollIntoView({ behavior: 'smooth' })
  }
}

onMounted(() => {
  window.addEventListener('mousemove', handleMouseMove)
})

onUnmounted(() => {
  window.removeEventListener('mousemove', handleMouseMove)
  uiState.isTornadoActive = false
})
</script>

<style scoped>
.sucked-in {
  animation: tornadoSuck 2s cubic-bezier(0.55, 0.055, 0.675, 0.19) forwards;
  transform-origin: center center;
  pointer-events: none; /* 动画过程中禁止交互 */
}

@keyframes tornadoSuck {
  0% {
    transform: scale(1) rotate(0deg);
    opacity: 1;
    filter: blur(0px);
  }
  40% {
    transform: scale(0.8) rotate(5deg);
    opacity: 0.9;
    filter: blur(1px);
  }
  100% {
    transform: scale(0) rotate(1080deg);
    opacity: 0;
    filter: blur(20px);
  }
}
</style>
